import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Ticket } from 'lucide-react';
import { SearchBar } from '@/components/ui/SearchBar';

interface RunnerFilterBarProps {
    searchTerm: string;
    onSearchChange: (value: string) => void;
    genderFilter: 'ALL' | 'MALE' | 'FEMALE';
    onGenderChange: (gender: 'ALL' | 'MALE' | 'FEMALE') => void;
    filteredCount: number;
    totalCount: number;
    /** Show "My bets" toggle */
    showMyBetsFilter?: boolean;
    myBetsOnly?: boolean;
    onMyBetsChange?: (value: boolean) => void;
}

const genderOptions = ['ALL', 'MALE', 'FEMALE'] as const;

export function RunnerFilterBar({
    searchTerm,
    onSearchChange,
    genderFilter,
    onGenderChange,
    filteredCount,
    totalCount,
    showMyBetsFilter,
    myBetsOnly,
    onMyBetsChange,
}: RunnerFilterBarProps) {
    const { t } = useTranslation('races');

    const genderLabel = (g: typeof genderOptions[number]) =>
        g === 'ALL' ? t('details.filter.all')
            : g === 'MALE' ? t('details.filter.men')
                : t('details.filter.women');

    return (
        <div className="sticky top-16 z-30 bg-white/95 backdrop-blur-sm pt-3 pb-2 space-y-2.5">
            <SearchBar
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                onClear={() => onSearchChange('')}
                placeholder={t('details.filter.searchPlaceholder')}
                className="[&_input]:h-11 [&_input]:text-sm"
            />

            <div className="flex items-center justify-between gap-3">
                {/* Segmented control — genre */}
                <div className="bg-slate-100 p-1 rounded-xl flex shadow-inner">
                    {genderOptions.map((g) => (
                        <button
                            key={g}
                            onClick={() => onGenderChange(g)}
                            className={cn(
                                "px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all",
                                genderFilter === g
                                    ? "bg-white text-emerald-700 shadow-sm ring-1 ring-black/5"
                                    : "text-slate-500 hover:text-slate-700"
                            )}
                        >
                            {genderLabel(g)}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-2">
                    {/* My bets toggle */}
                    {showMyBetsFilter && onMyBetsChange && (
                        <button
                            onClick={() => onMyBetsChange(!myBetsOnly)}
                            className={cn(
                                "flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-bold transition-all",
                                myBetsOnly
                                    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                                    : "bg-slate-100 text-slate-500 hover:text-slate-700"
                            )}
                        >
                            <Ticket size={12} />
                            {t('details.filter.myBets')}
                        </button>
                    )}

                    {/* Runner count */}
                    <span className="text-xs text-slate-400 font-medium whitespace-nowrap">
                        {filteredCount === totalCount
                            ? t('details.filter.countAll', { count: totalCount })
                            : t('details.filter.count', { filtered: filteredCount, total: totalCount })}
                    </span>
                </div>
            </div>
        </div>
    );
}
